import {Field, Form, FormElement, FormRenderProps} from "@progress/kendo-react-form"
import {useInternationalization} from "@progress/kendo-react-intl"
import {MDBCard, MDBCardBody, MDBCol, MDBRow} from "mdbreact"
import {useEffect, useState} from "react"

import {
    BookingParams,
    fetchBookingsDataAction,
    Overview,
    setBookingFiltersAction,
} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../store"
import DealerDropdown from "../Shared/dealerDropdown"
import BKGridFilterDatePicker from "./controls/bookingGridFilterDatePicker"

const MotBookingGridFilter = () => {
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const bookingFilters = useAppSelector((state: RootState) => state.containers.motBooking.bookingFilters)
    const schedules = useAppSelector((state: RootState) => state.application.schedulesForDealer)
    const dispatch = useAppDispatch()
    const overviews = useAppSelector((state: RootState) => state.containers.motBooking.overviews)

    const [filterParam, setFilterParams] = useState<BookingParams>({
        start: bookingFilters.start,
        selectedDealer: bookingFilters.selectedDealer,
    })
    const loading = useAppSelector((state: RootState) => state.containers.motBooking.gettingBookings)

    const onDateChange = (value: Date) => {
        setFilterParams((prevValue: any) => {
            return {
                ...prevValue,
                start: value,
            }
        })
    }
    useEffect(() => {
        if (selectedDealer && filterParam.start !== null) {
            setFilterParams((prevValue: any) => {
                return {
                    ...prevValue,
                    selectedDealer: selectedDealer,
                }
            })
            const newfilterParam = {...filterParam, selectedDealer}
            dispatch(setBookingFiltersAction(newfilterParam))
            dispatch(fetchBookingsDataAction(newfilterParam))
        }
    }, [selectedDealer, filterParam.start])

    const intl = useInternationalization()
    const formatCurrency = (amount: number) => intl.formatNumber(amount, "c")
    const formatPercentage = (value: number) => intl.formatNumber(value, {style: "percent", minimumFractionDigits: 2})

    const availabilityTheme = (utility: number) => {
        if (utility < 75) return "overview-available"
        else if (utility >= 75 && utility < 100) return "overview-warning"
        else return "overview-danger"
    }

    const pnlTheme = (utility: number) => {
        if (utility < 75) return "overview-danger"
        else if (utility >= 75 && utility < 100) return "overview-warning"
        else return "overview-available"
    }

    const OverviewRow = (overview: Overview) => {
        const used = overview.bookedDuration.split(":")
        const unused = overview.capacity.split(":")
        const avail = overview.totalDuration.split(":")
        return (
            <>
                <MDBCard className="shadow-1-soft shadow-0">
                    <MDBCardBody className="p-0 small d-flex p-2" style={{minHeight: "47px"}}>
                        {loading ? (
                            <div className="d-flex justify-content-center m-auto w-100">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="ps-2 pe-2 border-right-booking" style={{flex: "0 0 43%"}}>
                                    <div className="d-flex justify-content-between">
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                SCH
                                            </MDBRow>
                                            <MDBRow>{`${avail[0]}h ${avail[1]}m`}</MDBRow>
                                        </div>
                                        <div className="justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center  font-weight-bold text-muted">
                                                USED
                                            </MDBRow>
                                            <MDBRow>{`${used[0]}h ${used[1]}m`}</MDBRow>
                                        </div>
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center  font-weight-bold text-muted">
                                                AVAIL
                                            </MDBRow>
                                            <MDBRow
                                                className={`font-weight-bold ${availabilityTheme(
                                                    overview.utilisation,
                                                )}`}
                                            >
                                                {`${unused[0]}h ${unused[1]}m`}
                                            </MDBRow>
                                        </div>
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center  font-weight-bold text-muted">
                                                UTIL
                                            </MDBRow>
                                            <MDBRow>
                                                {formatPercentage(
                                                    overview.utilisation === null ? 0 : overview.utilisation / 100,
                                                )}
                                            </MDBRow>
                                        </div>
                                    </div>
                                </div>
                                <div className="ps-2 pe-2 border-right-booking" style={{flex: "0 0 43%"}}>
                                    <div className="d-flex justify-content-between">
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                DVSA
                                            </MDBRow>
                                            <MDBRow> {formatCurrency(overview.dvsaFee ?? 0)}</MDBRow>
                                        </div>
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                ACT
                                            </MDBRow>
                                            <MDBRow>{formatCurrency(overview.bookedTestFee ?? 0)}</MDBRow>
                                        </div>
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                P&L
                                            </MDBRow>
                                            <MDBRow
                                                className={`font-weight-bold ${pnlTheme(
                                                    overview.profitorLossPer ?? 0,
                                                )}`}
                                            >
                                                {formatCurrency(
                                                    overview.profitorLoss === null ? 0 : overview.profitorLoss,
                                                )}
                                            </MDBRow>
                                        </div>
                                        <div className=" justify-content-center mx-sm-3">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                UTIL
                                            </MDBRow>
                                            <MDBRow>
                                                {formatPercentage(
                                                    overview.profitorLossPer === null
                                                        ? 0
                                                        : overview.profitorLossPer / 100,
                                                )}
                                            </MDBRow>
                                        </div>
                                    </div>
                                </div>
                                <div className="ps-2 pe-2" style={{flex: "0 0 13%"}}>
                                    <div>
                                        <MDBCol className=" justify-content-center mx-sm-1" title="Lane fees">
                                            <MDBRow className="d-flex justify-content-center font-weight-bold  font-weight-bold text-muted">
                                                LANE FEE
                                            </MDBRow>
                                            <MDBRow className="d-flex justify-content-center">
                                                {formatCurrency(overview.bookedLaneFee)}
                                            </MDBRow>
                                        </MDBCol>
                                    </div>
                                </div>
                            </>
                        )}
                    </MDBCardBody>
                </MDBCard>
            </>
        )
    }

    return (
        <Form
            key={JSON.stringify(filterParam)}
            initialValues={filterParam}
            render={(formRenderProps: FormRenderProps) => (
                <div className="mb-2">
                    <MDBRow className="align-items-end">
                        <MDBCol md="2" sm="4">
                            <Field
                                name={"bk1testDate"}
                                component={BKGridFilterDatePicker}
                                val={filterParam.start}
                                updateStateValue={(value: Date) => {
                                    onDateChange(value)
                                }}
                                schedulesData={schedules}
                            />
                        </MDBCol>
                        <MDBCol md="2" sm="3">
                            <FormElement>
                                <Field name={"plannerDealerID"} component={DealerDropdown} />
                            </FormElement>
                        </MDBCol>
                        {overviews.length === 0 && (
                            <>
                                <MDBCol sm="1" className="pe-0">
                                    <MDBCard
                                        style={{minHeight: "48px"}}
                                        className="shadow-1-soft d-flex justify-content-around align-items-center font-weight-bold"
                                    >
                                        No SCH
                                    </MDBCard>
                                </MDBCol>
                                <MDBCol>
                                    <MDBCard
                                        style={{minHeight: "48px"}}
                                        className="shadow-1-soft shadow-0 d-flex justify-content-around align-items-center"
                                    >
                                        <div className="font-weight-bold text-muted">No Schedule for the day</div>
                                    </MDBCard>
                                </MDBCol>
                            </>
                        )}
                        {overviews != null && overviews.length > 0 && (
                            <>
                                <MDBCol sm="1" className="pe-0">
                                    <MDBCard
                                        style={{minHeight: "48px"}}
                                        className="shadow-1-soft d-flex justify-content-around align-items-center"
                                    >
                                        {loading ? (
                                            <div className="spinner-border spinner-border-sm" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        ) : (
                                            <div className=" justify-content-center mx-sm-3 small">
                                                <MDBRow className="d-flex justify-content-center font-weight-bold text-muted">
                                                    SCH
                                                </MDBRow>
                                                <MDBRow>{overviews[0].eventTimes}</MDBRow>
                                            </div>
                                        )}
                                    </MDBCard>
                                </MDBCol>
                                <MDBCol>
                                    <>{OverviewRow(overviews[0])}</>
                                </MDBCol>
                            </>
                        )}
                    </MDBRow>
                </div>
            )}
        />
    )
}
export default MotBookingGridFilter
